"use client";
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Gift,
  TrendingUp,
  Users,
  Clock,
  Star,
  Zap,
  Trophy,
  Share2,
  Loader2,
} from "lucide-react";
import DisplayCards from "@/components/ui/display-cards";
import {
  rewardsService,
  CurrentUserRewardResponse,
} from "@/services/rewards.service";
import { toast } from "react-hot-toast";
import { capitalize } from "lodash";
import { formatCurrency } from "@/utils/formatters";

export default function RewardsLanding() {
  const [rewardsData, setRewardsData] =
    useState<CurrentUserRewardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);

  useEffect(() => {
    const fetchRewardsData = async () => {
      try {
        setIsLoading(true);
        const data = await rewardsService.getCurrentUserRewards();
        setRewardsData(data);
      } catch (error) {
        console.error("Failed to fetch rewards data:", error);
        toast.error("Failed to load rewards data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRewardsData();
  }, []);

  const handleGenerateShareLink = async () => {
    try {
      setIsGeneratingToken(true);
      const { shareUrl } = await rewardsService.generateShareToken();

      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard!");
    } catch (error) {
      console.error("Failed to generate share link:", error);
      toast.error("Failed to generate share link");
    } finally {
      setIsGeneratingToken(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" aria-busy="true">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-label="Loading rewards" />
      </div>
    );
  }

  if (!rewardsData) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-[15px] font-semibold text-foreground">Rewards couldn't load</p>
        <p className="max-w-sm text-sm text-muted-foreground">Check your connection and try again.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </div>
    );
  }

  const currentTier = rewardsData.rewardTiers[rewardsData.currentTierIndex];
  const nextTier =
    rewardsData.nextTierIndex !== null
      ? rewardsData.rewardTiers[rewardsData.nextTierIndex]
      : null;
  const progress = rewardsData.progress;
  const remainingToNext = rewardsData.remainingToNext;
  const currentUserRevenue = rewardsData.totalEarnings;
  const currentTierIndex = rewardsData.currentTierIndex;
  const nextTierIndex = rewardsData.nextTierIndex;
  const milestoneRewards = rewardsData.rewardTiers;
  const hasAchievedTier = rewardsData.hasAchievedTier ?? true; // For backward compatibility

  // Map leaderboard data to card format
  const leaderboardCards = rewardsData.topLeaderboard.map((entry, index) => {
    const baseCard = {
      avatar: "/professional-business-avatar.png", // You can customize this per entry if needed
      title: entry.name,
      description: `$${entry.earnings.toLocaleString()} USD GENERATED`,
      date: entry.tier,
      titleClassName: "text-foreground",
    };

    // Apply different styles for each position
    if (index === 0) {
      return {
        ...baseCard,
        className:
          "[grid-area:stack] hover:-translate-y-10 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0",
      };
    } else if (index === 1) {
      return {
        ...baseCard,
        className:
          "[grid-area:stack] translate-x-12 translate-y-10 hover:-translate-y-1 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0",
      };
    } else {
      return {
        ...baseCard,
        className:
          "[grid-area:stack] translate-x-24 translate-y-20 hover:translate-y-10",
      };
    }
  });

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-brand-600 via-brand-500 to-pink-500" />
      <div className="bg-black/20 absolute inset-0" />

      <div className="relative z-10 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Milestone rewards
          </h1>
          <p className="text-sm sm:text-base text-white/80">
            Earn Exclusive Rewards As You Grow.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          <div className="lg:col-span-8 space-y-4 sm:space-y-6">
            {/* Current Status Card */}
            <Card
              id="RL-current-status-card"
              className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8"
            >
              <h2 className="text-base sm:text-lg font-semibold text-white/90 mb-4 sm:mb-6">
                Current Rewards Card
              </h2>

              <div className="bg-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-brand-100 to-brand-200 rounded-xl sm:rounded-2xl flex items-center justify-center text-3xl sm:text-4xl shadow-lg">
                    <img
                      className="w-full h-full object-stretch rounded-xl"
                      src={`/reward-assets/reward-pictures/${currentTierIndex + 1}.png`}
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <p className="text-xs sm:text-sm text-muted-foreground mb-1">
                      ${currentUserRevenue.toLocaleString()} revenue generated
                    </p>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 sm:mb-4 tracking-tight">
                      {currentTier.tier.toUpperCase()}{" "}
                    </h3>

                    {/* Progress Bar */}
                    <div id="RL-progress-bar" className="mb-2 sm:mb-3">
                      <div className="w-full bg-gradient-to-r from-brand-100 to-pink-100 rounded-full h-2 sm:h-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-brand-600 to-pink-500 h-2 sm:h-3 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col xs:flex-row justify-between gap-1 xs:gap-0 text-xs sm:text-sm text-muted-foreground">
                      <span className="font-medium">
                        ${currentUserRevenue.toLocaleString()} generated
                      </span>
                      <span className="font-medium">
                        ${remainingToNext.toLocaleString()} left till next level
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-brand-50 to-pink-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-brand-100">
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {hasAchievedTier ? (
                      <>
                        Congratulations on reaching the {currentTier.tier}{" "}
                        milestone! Check your email for instructions on how to
                        claim your {currentTier.rewards[0]}.Keep up the amazing
                        work & best of luck crushing your next milestone!
                      </>
                    ) : (
                      <>
                        Welcome! Start earning to unlock your first reward tier:{" "}
                        {currentTier.tier}. You need $
                        {currentTier.threshold.toLocaleString()} in revenue to
                        unlock {currentTier.rewards[0]}. Keep going!
                      </>
                    )}
                  </p>
                </div>
              </div>

              <Button
                id="RL-share-button"
                className="w-full bg-card hover:bg-card/90 text-foreground font-semibold py-4 sm:py-6 rounded-xl sm:rounded-2xl text-sm sm:text-base transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                onClick={handleGenerateShareLink}
                disabled={isGeneratingToken}
              >
                {isGeneratingToken ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    Share
                  </>
                )}
              </Button>
            </Card>

            {/* ReveShare Leaderboard */}
            <Card
              id="RL-leaderboard-section"
              className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <Trophy className="w-5 h-5 text-white" />
                <h2 className="text-base sm:text-lg font-semibold text-white">
                  ReveShare Leaderboard
                </h2>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-white/80 w-1/3">
                  These are the top earners on Reveshare. The more you earn, the
                  higher you climb & the easier it is for brands, affiliates &
                  agencies to find you! Hard work pays off!
                </span>
                <div className="flex-1 xl:-translate-y-[36%] xl:scale-105 xl:translate-x-[14%]">
                  <DisplayCards cards={leaderboardCards} />
                </div>
              </div>
            </Card>

            {/* Stats Cards */}
            <div
              id="RL-stats-cards"
              className="grid grid-cols-1 xs:grid-cols-3 gap-3 sm:gap-4"
            >
              <Card className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:scale-[1.02] transition-transform duration-300">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-card/20 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-white">
                      {hasAchievedTier
                        ? rewardsData.usersWonCurrentReward.toLocaleString()
                        : "0"}
                    </p>
                    <p className="text-xs text-white/80">
                      Milestone{" "}
                      {`${
                        rewardsData.usersWonCurrentReward ? "" : "'s"
                      } unlocked`}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:scale-[1.02] transition-transform duration-300">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-card/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-white">
                      {currentTier.tier}
                    </p>
                    <p className="text-xs text-white/80">Current Tier</p>
                  </div>
                </div>
              </Card>

              <Card className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:scale-[1.02] transition-transform duration-300">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-card/20 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-white">
                      ${remainingToNext.toLocaleString()}
                    </p>
                    <p className="text-xs text-white/80">To Next Tier</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <div className="lg:col-span-4">
            <Card
              id="RL-milestone-sidebar"
              className="backdrop-blur-xl bg-card/15 border border-white/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:sticky lg:top-8"
            >
              <h2 className="text-base sm:text-lg font-semibold text-white mb-2">
                Milestone Rewards
              </h2>
              <p className="text-xs sm:text-sm text-white/80 mb-4 sm:mb-6">
                Your journey to the top
              </p>

              <div className="space-y-2 sm:space-y-3">
                {milestoneRewards.map((milestone, index) => {
                  const isCompleted = currentUserRevenue >= milestone.threshold;
                  const isCurrent =
                    index === currentTierIndex && hasAchievedTier;
                  const isNext = index === nextTierIndex;

                  return (
                    <div key={index} className="relative">
                      {isNext && nextTierIndex !== null && (
                        <div className="text-center">
                          <span className="text-xs font-semibold text-white/90 bg-card/20 px-3 py-1 rounded-full">
                            Next level
                          </span>
                        </div>
                      )}
                      <div
                        className={`rounded-xl sm:rounded-2xl p-3 sm:p-4 transition-all duration-300 ${
                          isCurrent
                            ? "bg-card border-2 border-white shadow-lg scale-105"
                            : isCompleted
                            ? "bg-card/90 border border-white/50"
                            : "bg-card/20 border border-white/30"
                        } ${isNext ? "mt-4 sm:mt-6" : ""}`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center text-xl sm:text-2xl ${
                              isCurrent || isCompleted
                                ? "bg-gradient-to-br from-brand-100 to-pink-100"
                                : "bg-card/20"
                            }`}
                          >
                            <img
                              className="w-full h-full object-stretch rounded-xl"
                              src={`/reward-assets/reward-pictures/${
                                index + 1
                              }.png`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3
                                className={`text-sm sm:text-base font-bold ${
                                  isCurrent || isCompleted
                                    ? "text-foreground"
                                    : "text-white"
                                }`}
                              >
                                {milestone.tier}
                              </h3>
                              <span
                                className={`text-xs sm:text-sm font-semibold ${
                                  isCurrent || isCompleted
                                    ? "text-foreground"
                                    : "text-white"
                                }`}
                              >
                                {formatCurrency(milestone.threshold)}
                              </span>
                            </div>
                            <p
                              className={`text-xs truncate ${
                                isCurrent || isCompleted
                                  ? "text-muted-foreground"
                                  : "text-white/80"
                              }`}
                            >
                              {milestone.rewards.map((e) => {
                                return (
                                  <div key={e}>
                                    - {capitalize(e.replace(/_/g, " "))}
                                  </div>
                                );
                              })}
                            </p>
                          </div>
                          {isCompleted && (
                            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <Star className="w-3 h-3 sm:w-4 sm:h-4 text-white fill-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Next Milestone Info */}
              {nextTier && (
                <Card
                  id="RL-next-reward-info"
                  className="mt-4 sm:mt-6 bg-gradient-to-br from-yellow-400/20 to-orange-400/20 border border-amber-900/30 rounded-xl sm:rounded-2xl p-3 sm:p-4"
                >
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-amber-900" />
                    <h3 className="text-xs sm:text-sm font-semibold text-amber-900">
                      Next Reward
                    </h3>
                  </div>
                  <p className="text-xs text-amber-900/90 mb-2">
                    Only{" "}
                    <span className="font-bold">
                      ${remainingToNext.toLocaleString()}
                    </span>{" "}
                    away from unlocking:
                  </p>
                  <div className="space-y-1">
                    {nextTier.rewards.map((reward, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Gift className="w-3 h-3 text-amber-900/80" />
                        <span className="text-xs text-amber-900/90">
                          {capitalize(reward.replace(/_/g, " "))}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {!nextTier && (
                <Card className="mt-4 sm:mt-6 bg-gradient-to-br from-green-400/20 to-emerald-400/20 border border-green-400/30 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-green-300" />
                    <h3 className="text-xs sm:text-sm font-semibold text-white">
                      Maximum Tier Achieved!
                    </h3>
                  </div>
                  <p className="text-xs text-white/90">
                    Congratulations! You've reached the highest reward tier
                    available.
                  </p>
                </Card>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
